import { cardPos } from '@shared/lib';
import { AxiosError } from 'axios';
import { api } from '@shared/api';
import type {
  BuilderKinds,
  CardState,
  CramMode,
  ExerciseKind,
  SessionCard,
  SessionItemStatus,
  SessionProgressItem,
} from '../model/types';

// ---- Backend DTO (snake_case) ----
interface BackendSessionCard {
  item_id: string;
  user_card_state_id: string;
  card_id: string;
  front_name: string;
  transcription: string | null;
  pos: string | null;
  is_llm_generated: boolean;
  fsrs_state: CardState;
  selected_translations: string[];
  // Переводы смысла этого показа (2026-10-06); у старого бэка нет.
  sense_translations?: string[];
  // «Редкие» переводы строки словаря (2026-09-23): засчитываются, вопросом не бывают.
  rare_translations?: string[];
  selection_options: string[] | null;
  hint: string | null;
  definition: string | null;
  example_en: string | null;
  /** Фразовый глагол — подпись «phrasal verb» вместо verb. */
  is_phrasal_verb?: boolean;
  /** Свой пример для «Пропущено слово» (2026-10-06); null у старых карточек — берём example_en. */
  cloze_example_en?: string | null;
  /** Где в нём слово: [начало, конец). Один отрезок — пропуск вырезается по нему (went out). */
  cloze_example_spans?: [number, number][];
  /** Перевод пропуска и где в нём изучаемый перевод (2026-10-07) — после ответа. */
  cloze_example_ru?: string | null;
  cloze_example_ru_spans?: Array<[number, number]>;
  // Простое предложение прямого перевода и где в нём слово (2026-09-23).
  simple_example_en?: string | null;
  simple_example_spans?: Array<[number, number]>;
  // Перевод простого предложения с изучаемым переводом и где он в нём (2026-09-26).
  simple_example_ru?: string | null;
  simple_example_ru_spans?: Array<[number, number]>;
  en_synonyms: string[];
  position_in_queue: number;
  total_pending: number;
}

interface BackendSessionItem {
  item_id: string;
  status: SessionItemStatus;
  attempts: number;
}

interface BackendSession {
  id: string;
  status: SessionStatus;
  // Модуль тренировки. null оставлен для совместимости со старыми DTO.
  module_id?: string | null;
  total: number;
  remaining: number;
  current_card: BackendSessionCard | null;
  // Прогресс по каждому item (по position). Старый бэк поля не шлёт → []
  // (resume деградирует к «прогресс с нуля», но не падает).
  items?: BackendSessionItem[];
  cram_mode: CramMode | null;
  // Состав упражнений режима «Тренировка»: ['random'] или подмножество
  // input/reverse/mcq/cloze. null/отсутствует у остальных сессий.
  exercise_kinds?: string[] | null;
  // Включён ли финальный матч. Старый бэк поля не шлёт → считаем, что включён.
  with_boss?: boolean;
  // Начисления за тренировку (/finish, /complete; с 2026-10-22). Старый бэк поля не шлёт.
  bonus_granted?: BackendBonusGrant[];
}

interface BackendBonusGrant {
  amount: number;
  reason: BonusGrant['reason'];
  streak?: number | null;
}

interface BackendOffSense {
  sense_id: string;
  hint: string | null;
  score: number;
}

interface BackendAnswer {
  is_correct: boolean;
  // 'typo' попадает сюда из бэкового rapidfuzz-шага (см. TranslationMatchService).
  matched_via: 'exact' | 'typo' | 'semantic' | null;
  score: number | null;
  off_sense: BackendOffSense | null;
  // Засчитано переводом другого смысла слова: какой имелся в виду в предложении.
  context_note?: { expected: string; matched: string | null } | null;
  // Засчитано переводом всей фразы: в нём есть перевод выделенного слова (2026-10-29).
  whole_sentence?: boolean;
  session_status: SessionStatus;
  next_card: BackendSessionCard | null;
  bonus_granted?: BackendBonusGrant[];
}

interface BackendBossPoolItem {
  // Поля state_id/pos появились вместе с multi-POS-фиксом. До рестарта старый
  // backend их не шлёт — типизируем как optional, чтобы рантайм-фолбэк
  // на card_id в mapping был type-safe.
  state_id?: string;
  card_id: string;
  front_name: string;
  pos?: string | null;
  ru: string;
  // Простое предложение смысла пары (2026-10-06); у ручных и старых карточек нет.
  sentence?: string | null;
  sentence_spans?: [number, number][];
}

interface BackendBossPool {
  items: BackendBossPoolItem[];
}

// ---- Public types (camelCase, used by UI) ----
export type SessionStatus =
  | 'in_progress'
  | 'is_ready_to_boss'
  | 'completed'
  | 'abandoned';

export interface SessionView {
  id: string;
  status: SessionStatus;
  /** Модуль тренировки. null допустим только в старых DTO. */
  moduleId: string | null;
  total: number;
  remaining: number;
  currentCard: SessionCard | null;
  /** Прогресс по каждому item очереди (по позиции). На /start все pending;
   *  при resume — смесь, из которой движок восстанавливает точки и счётчики. */
  progress: SessionProgressItem[];
  /** Выбранный режим тренировки; прежние значения поддерживаются для resume. */
  cramMode: CramMode | null;
  /** Состав упражнений режима «Тренировка» (cramMode='builder'): 'random' —
   *  случайный микс, массив — выбранные типы. null для остальных сессий.
   *  Приходит с бэка → переживает перезагрузку/resume. */
  builderKinds: BuilderKinds;
  /** Нужен ли финальный матч. false — очередь закончится сразу итогами
   *  (конструктор выключил матч); /complete в этом случае не вызываем. */
  withBoss: boolean;
  /** Несгораемые жетоны, начисленные этим запросом (/finish, /complete). */
  bonusGranted: BonusGrant[];
}

/** Начисление несгораемых жетонов: серия дней, возвращение после перерыва, «Задание дня» (2026-10-22). */
export interface BonusGrant {
  amount: number;
  reason: 'streak' | 'comeback' | 'daily_task';
  /** Длина серии — у reason='streak'. */
  streak: number | null;
}

function mapBonus(list: BackendBonusGrant[] | undefined): BonusGrant[] {
  return (list ?? []).map((g) => ({ amount: g.amount, reason: g.reason, streak: g.streak ?? null }));
}

export interface OffSenseHint {
  senseId: string;
  hint: string | null;
  score: number;
}

export type MatchedVia = 'exact' | 'typo' | 'semantic';

/** Прямой перевод по простому предложению засчитан переводом другого смысла слова:
 *  в этом предложении имелся в виду expected (matched — смысл, которым засчитано). */
export interface ContextNote {
  expected: string;
  matched: string | null;
}

export interface AnswerView {
  isCorrect: boolean;
  matchedVia: MatchedVia | null;
  score: number | null;
  offSense: OffSenseHint | null;
  contextNote: ContextNote | null;
  /** Засчитано переводом всей фразы вопроса: в нём есть перевод выделенного слова. */
  wholeSentence: boolean;
  sessionStatus: SessionStatus;
  nextCard: SessionCard | null;
  /** Несгораемые жетоны, начисленные этим ответом (возвращение, конец тренировки). */
  bonusGranted: BonusGrant[];
}

export interface BossPoolItem {
  /** user_card_state.id — уникальный per-POS ключ. Используется как id для
   *  match-button'а и для дедупа в makeBossPacks. cardId сам по себе НЕ
   *  уникален: у multi-POS слова один cardId на verb + noun, и до этого фикса
   *  они схлопывались в один айтем boss-пула. */
  stateId: string;
  cardId: string;
  frontName: string;
  pos: string | null;
  ru: string;
  /** Простое предложение смысла, чей перевод в паре, — слева вместо слова: три «top»
   *  разных частей речи без него не различить (2026-10-06). null — показываем слово. */
  sentence: string | null;
  sentenceSpans: [number, number][];
}

export type StartErrorCode = 'ACTIVE_EXISTS' | 'NO_CARDS' | 'NETWORK' | 'UNKNOWN';

export class StartSessionError extends Error {
  constructor(public code: StartErrorCode) {
    super(code);
    this.name = 'StartSessionError';
  }
}

// ----------------------------------------------------------------------
// Mapping helpers
// ----------------------------------------------------------------------
/** Пропуск по месту слова, которое нашёл бэк (формы тоже: went out). Не один отрезок — null. */
function clozeBySpan(sentence: string | null | undefined, spans: [number, number][] | undefined): string | null {
  if (!sentence || !spans || spans.length !== 1) return null;
  const [start, end] = spans[0];
  if (start < 0 || end > sentence.length || start >= end) return null;
  return sentence.slice(0, start) + '___' + sentence.slice(end);
}

function deriveCloze(exampleEn: string | null, frontName: string): string | null {
  // Cloze-упражнение требует предложение с прочерком на месте целевого слова.
  // Если у карточки нет example_en — возвращаем null, чтобы exerciseFor мог
  // зафолбэчить такую карточку обратно на input или mcq. Литеральный
  // fallback вида 'I ___ this.' раньше показывался юзеру и сбивал с толку.
  if (!exampleEn) return null;
  const escaped = frontName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return exampleEn.replace(new RegExp(`\\b${escaped}\\w*`, 'gi'), '___');
}

function mapCard(c: BackendSessionCard): SessionCard {
  // ru — главный «правильный ответ» для exact-fallback и для отображения в
  // GoodCue/WrongCue. Семантическая/typo проверка идёт на бэке против всего
  // списка selectedTranslations, но UI показывает первый как «эталон».
  const ru = c.selected_translations[0] ?? '';
  return {
    itemId: c.item_id,
    userCardStateId: c.user_card_state_id,
    cardId: c.card_id,
    id: c.item_id,
    en: c.front_name,
    ipa: c.transcription ?? '',
    ru,
    state: c.fsrs_state,
    cloze: clozeBySpan(c.cloze_example_en, c.cloze_example_spans) ?? deriveCloze(c.cloze_example_en ?? c.example_en, c.front_name),
    isLlmGenerated: c.is_llm_generated,
    pos: cardPos(c.pos, c.is_phrasal_verb),
    selectedTranslations: c.selected_translations,
    senseTranslations: c.sense_translations ?? [],
    rareTranslations: c.rare_translations ?? [],
    selectionOptions: c.selection_options,
    hint: c.hint,
    definition: c.definition,
    exampleEn: c.example_en,
    simpleExample: c.simple_example_en ?? null,
    simpleExampleSpans: c.simple_example_spans ?? [],
    simpleExampleRu: c.simple_example_ru ?? null,
    simpleExampleRuSpans: c.simple_example_ru_spans ?? [],
    clozeRu: c.cloze_example_ru ?? null,
    clozeRuSpans: c.cloze_example_ru_spans ?? [],
    enSynonyms: c.en_synonyms,
    positionInQueue: c.position_in_queue,
    totalPending: c.total_pending,
  };
}

const BUILDER_KINDS_VALID: ExerciseKind[] = ['input', 'reverse', 'mcq', 'cloze'];

/** Парсит exercise_kinds с бэка в состав «Тренировки». ['random'] → 'random';
 *  массив типов → отфильтрованный список; пусто/мусор → null (exerciseFor тогда
 *  упадёт в дефолтный random-микс, см. buildPool). */
function parseBuilderKinds(raw?: string[] | null): BuilderKinds {
  if (!raw || raw.length === 0) return null;
  if (raw.includes('random')) return 'random';
  const picked = raw.filter((k): k is ExerciseKind =>
    (BUILDER_KINDS_VALID as string[]).includes(k),
  );
  return picked.length ? picked : null;
}

function mapSession(s: BackendSession): SessionView {
  return {
    id: s.id,
    status: s.status,
    moduleId: s.module_id ?? null,
    total: s.total,
    remaining: s.remaining,
    currentCard: s.current_card ? mapCard(s.current_card) : null,
    progress: (s.items ?? []).map((i) => ({
      itemId: i.item_id,
      status: i.status,
      attempts: i.attempts,
    })),
    cramMode: s.cram_mode ?? null,
    builderKinds: parseBuilderKinds(s.exercise_kinds),
    withBoss: s.with_boss ?? true,
    bonusGranted: mapBonus(s.bonus_granted),
  };
}

function mapAnswer(a: BackendAnswer): AnswerView {
  return {
    isCorrect: a.is_correct,
    matchedVia: a.matched_via,
    score: a.score,
    offSense: a.off_sense
      ? {
          senseId: a.off_sense.sense_id,
          hint: a.off_sense.hint,
          score: a.off_sense.score,
        }
      : null,
    contextNote: a.context_note
      ? { expected: a.context_note.expected, matched: a.context_note.matched ?? null }
      : null,
    wholeSentence: !!a.whole_sentence,
    sessionStatus: a.session_status,
    nextCard: a.next_card ? mapCard(a.next_card) : null,
    bonusGranted: mapBonus(a.bonus_granted),
  };
}

// ----------------------------------------------------------------------
// REST
// ----------------------------------------------------------------------
export async function fetchActiveSession(): Promise<SessionView | null> {
  const { data } = await api.get<BackendSession | null>('/sessions/active');
  if (data === null) return null;
  return mapSession(data);
}

/** Активная тренировка выбранного модуля. /active отдельно возвращает
 *  последнюю тренировку среди всех модулей для главной страницы. */
export async function fetchActiveCramSession(
  moduleId: string,
): Promise<SessionView | null> {
  const { data } = await api.get<BackendSession | null>(
    `/sessions/cram/${moduleId}/active`,
  );
  if (data === null) return null;
  return mapSession(data);
}

/** Сохранённый состав «Тренировки» модуля (упражнения + выбранные карточки),
 *  чтобы конструктор не сбрасывался между заходами. null — ещё ничего не
 *  собирали (показываем дефолты). stateIds=null означает «все карточки». */
export interface BuilderSettings {
  exercises: string[];
  /** Выбранные карточки по user_card_state.id. null = все карточки модуля. */
  stateIds: string[] | null;
  /** Был ли включён финальный матч в прошлый раз. */
  withBoss: boolean;
}

interface BackendBuilderSettings {
  exercises: string[];
  state_ids: string[] | null;
  with_boss?: boolean;
}

export async function fetchBuilderSettings(
  moduleId: string,
): Promise<BuilderSettings | null> {
  const { data } = await api.get<BackendBuilderSettings | null>(
    `/sessions/cram/${moduleId}/builder-settings`,
  );
  if (data === null) return null;
  return {
    exercises: data.exercises,
    stateIds: data.state_ids ?? null,
    withBoss: data.with_boss ?? true,
  };
}

export interface CramCounts {
  recent: number;
  weak: number;
  whole: number;
  learned: number;
}

export async function fetchCramCounts(moduleId: string): Promise<CramCounts> {
  const { data } = await api.get<CramCounts>(`/sessions/cram/${moduleId}/counts`);
  return data;
}

/** Historical answers belong to an individual user card state, not card_id. */
export interface CramCardStats {
  stateId: string;
  attemptCount: number;
  mistakeCount: number;
}

interface BackendCramCardStats {
  items: {
    state_id: string;
    attempt_count: number;
    mistake_count: number;
  }[];
}

export async function fetchCramCardStats(moduleId: string): Promise<CramCardStats[]> {
  const { data } = await api.get<BackendCramCardStats>(
    `/sessions/cram/${moduleId}/card-stats`,
  );
  return data.items.map((item) => ({
    stateId: item.state_id,
    attemptCount: item.attempt_count,
    mistakeCount: item.mistake_count,
  }));
}

/** Строка списка «На чём потренируемся?»: что повторить сегодня и завтра по истории тренировок
 *  (2026-10-21, не по FSRS). repeat = mistakes + stale. */
export interface TrainingModulePlan {
  moduleId: string;
  title: string;
  ready: number;
  new: number;
  mistakes: number;
  stale: number;
  repeat: number;
  doneToday: number;
  tomorrow: number;
  trained: boolean;
}

interface BackendTrainingOverview {
  modules: {
    module_id: string;
    title: string;
    ready: number;
    new: number;
    mistakes: number;
    stale: number;
    repeat: number;
    done_today: number;
    tomorrow: number;
    trained: boolean;
  }[];
}

/** Все доступные модули, сверху — где есть что повторить. */
export async function fetchTrainingOverview(): Promise<TrainingModulePlan[]> {
  const { data } = await api.get<BackendTrainingOverview>('/sessions/cram/overview');
  return data.modules.map((module) => ({
    moduleId: module.module_id,
    title: module.title,
    ready: module.ready,
    new: module.new,
    mistakes: module.mistakes,
    stale: module.stale,
    repeat: module.repeat,
    doneToday: module.done_today,
    tomorrow: module.tomorrow,
    trained: module.trained,
  }));
}

/** Набор «На сегодня» в конструкторе: сначала повторить, потом новые (порядок важен). */
export interface TodayCards {
  stateIds: string[];
  repeat: number;
  new: number;
}

export async function fetchCramToday(moduleId: string): Promise<TodayCards> {
  const { data } = await api.get<{ state_ids: string[]; repeat: number; new: number }>(
    `/sessions/cram/${moduleId}/today`,
  );
  return { stateIds: data.state_ids, repeat: data.repeat, new: data.new };
}

/** Опции режима «Тренировка» (mode='builder'): что собрал юзер. exercises —
 *  выбранные типы со случайным порядком; randomMix — совместимость со старым
 *  выбором всех типов; stateIds — выбранные
 *  карточки (по user_card_state.id). stateIds undefined/пусто → весь модуль (шлём
 *  null). Для остальных режимов не передаются. */
export interface CramStartOptions {
  exercises?: ExerciseKind[];
  randomMix?: boolean;
  stateIds?: string[];
  /** false — тренировка кончается итогами без финального матча. */
  withBoss?: boolean;
  /** Сколько карточек взять из набора (1–100); не задано — весь набор. Первая тренировка
   *  знакомства — 5. Старый бэкенд поле молча игнорирует (схема без extra=forbid). */
  limit?: number;
}

export async function startCramSession(
  moduleId: string,
  mode: CramMode,
  opts: CramStartOptions = {},
): Promise<SessionView> {
  try {
    // Тело снейк-кейсим вручную: axios снейк-кейсит только ОТВЕТЫ, не запросы.
    // state_ids: null = весь модуль (бэк так и трактует); непустой список — только
    // эти карточки. Пустой список бэк отверг бы 422 — UI не даёт стартовать с 0.
    const { data } = await api.post<BackendSession>('/sessions/cram/start', {
      module_id: moduleId,
      mode,
      exercises: opts.exercises ?? [],
      random_mix: opts.randomMix ?? false,
      state_ids: opts.stateIds && opts.stateIds.length > 0 ? opts.stateIds : null,
      with_boss: opts.withBoss ?? true,
      ...(opts.limit !== undefined ? { limit: opts.limit } : {}),
    });
    return mapSession(data);
  } catch (err) {
    if (err instanceof AxiosError) {
      const code = (err.response?.data as { error?: { code?: string } })?.error?.code;
      if (code === 'NO_CARDS_TO_LEARN') throw new StartSessionError('NO_CARDS');
      if (!err.response) throw new StartSessionError('NETWORK');
    }
    throw new StartSessionError('UNKNOWN');
  }
}

/** Потолок длины ответа. Зеркалит `max_user_input_chars` в конфиге бэкенда
 *  (AnswerRequestSchema.user_input max_length): поле не даёт набрать больше,
 *  поэтому юзер не упирается в 422 и не разносит вёрстку длинной строкой. */
export const ANSWER_MAX_CHARS = 128;

export async function submitAnswer(
  sessionId: string,
  userInput: string,
  // 'en' для cloze/reverse (юзер вводит само слово), 'ru' для input/mcq (перевод).
  // Дефолт 'ru' сохраняет обратную совместимость со старыми вызовами.
  answerLang: 'ru' | 'en' = 'ru',
  // true — кнопка «не помню»: бэк не матчит ввод, грейдит как неверно.
  // user_input всё равно обязателен (min_length=1) — шлём текущий ввод как есть.
  giveUp = false,
  // Тип упражнения: нужен бэку на англ. пути, чтобы развести строгость — reverse
  // засчитывает синонимы карточки, cloze только само слово (см. match_english).
  exercise?: ExerciseKind,
): Promise<AnswerView> {
  // give_up игнорирует содержимое, но user_input на схеме min_length=1 — если
  // юзер нажал «не помню» с пустым полем, шлём заглушку, чтобы не словить 422.
  const payload = giveUp && !userInput.trim() ? '—' : userInput;
  const { data } = await api.post<BackendAnswer>(
    `/sessions/${sessionId}/answer`,
    { user_input: payload, answer_lang: answerLang, give_up: giveUp, exercise },
  );
  return mapAnswer(data);
}

export async function completeSession(sessionId: string): Promise<SessionView> {
  const { data } = await api.post<BackendSession>(`/sessions/${sessionId}/complete`);
  return mapSession(data);
}

/** Досрочно закончить упражнения, сохранив ответы. Финал включает только
 *  карточки с попытками; без ответов сервер сразу возвращает completed. */
export async function finishSession(sessionId: string): Promise<SessionView> {
  const { data } = await api.post<BackendSession>(`/sessions/${sessionId}/finish`);
  return mapSession(data);
}

export async function abandonSession(sessionId: string): Promise<void> {
  await api.delete(`/sessions/${sessionId}`);
}

export async function fetchBossPool(sessionId: string): Promise<BossPoolItem[]> {
  const { data } = await api.get<BackendBossPool>(`/sessions/${sessionId}/boss-pool`);
  return data.items.map((i) => ({
    // Defensive: если бэк ещё не накатил multi-POS фикс (нет state_id в ответе),
    // фолбэкаемся на card_id — для single-POS карточек это эквивалентно и UI
    // не схлопывается в один айтем. Multi-POS (`mark`/verb + `mark`/noun)
    // в этом режиме не разделится, но и не сломает остальное.
    stateId: i.state_id ?? i.card_id,
    cardId: i.card_id,
    frontName: i.front_name,
    pos: i.pos ?? null,
    ru: i.ru,
    sentence: i.sentence ?? null,
    sentenceSpans: i.sentence_spans ?? [],
  }));
}

export type AnswerErrorKind =
  | 'session_desync'    // 409 — сессия не in_progress, нужен resume
  | 'rate_limited'      // 429 — per-user limit
  | 'embed_unavailable' // 503 EMBEDDING_SERVICE_UNAVAILABLE — бэк специально не применил FSRS
  | 'too_long'          // 422 — max_length=128 на user_input
  | 'server'            // прочие 5xx
  | 'network';          // нет ответа от сервера

export interface AnswerError {
  kind: AnswerErrorKind;
  /** Ключ текста (`session.answerError.*`): строку рисует сцена через t() — RU и EN (B13, 2026-10-20). */
  key: string;
  params?: Record<string, string | number>;
  retryAfterSeconds?: number;
}

/** Классификатор ошибок /answer для UI. Возвращает ключ текста и тип,
 *  по которому Session.tsx решает: показать строку и дать переотправить,
 *  или сбросить состояние и перезагрузить сессию (для session_desync). */
export function categorizeAnswerError(err: unknown): AnswerError {
  if (!(err instanceof AxiosError)) {
    return { kind: 'server', key: 'session.answerError.unknown' };
  }
  if (!err.response) {
    return { kind: 'network', key: 'session.answerError.network' };
  }
  const status = err.response.status;
  if (status === 409) {
    return { kind: 'session_desync', key: 'session.answerError.desync' };
  }
  if (status === 422) {
    return { kind: 'too_long', key: 'session.answerError.tooLong' };
  }
  if (status === 429) {
    const detail = (err.response.data as { detail?: string })?.detail;
    const sec = parseInt(detail?.match(/(\d+)\s*seconds?/)?.[1] ?? '0', 10);
    return sec > 0
      ? { kind: 'rate_limited', key: 'session.answerError.rateLimited', params: { n: sec }, retryAfterSeconds: sec }
      : { kind: 'rate_limited', key: 'session.answerError.rateLimitedShort' };
  }
  const code = (err.response.data as { error?: { code?: string } })?.error?.code;
  if (status === 503 && code === 'EMBEDDING_SERVICE_UNAVAILABLE') {
    return { kind: 'embed_unavailable', key: 'session.answerError.checkUnavailable' };
  }
  return { kind: 'server', key: 'session.answerError.server' };
}
